import React, { useCallback, useMemo } from 'react';
import { NavLinkProps, useLocation } from 'react-router-dom';

import { useCustomer } from 'common/hooks/useCustomer/useCustomer.hook';

import { routes } from 'routes/routes.constants';

import { memoizedComponent } from 'utils/memoizeComponent';

import { ButtonProps } from '../Button/Button.component';

export type AuthProtectedButtonProps = Partial<NavLinkProps> &
  Partial<ButtonProps> & {
    disabled?: boolean;
    redirectUrl?: string;
    children: React.ReactElement;
    forceUnAuthorizedUser?: boolean;
    pathname?: string;
  };

export const AuthProtectedButton: React.FC<React.PropsWithChildren<AuthProtectedButtonProps>> = memoizedComponent(
  ({ forceUnAuthorizedUser, disabled, redirectUrl, children, pathname: pathnameFromProps, ...props }) => {
    const { pathname } = useLocation();
    const { isAuthenticated } = useCustomer();

    const failedAuthCheck = useMemo(
      () => forceUnAuthorizedUser || (!isAuthenticated && !disabled),
      [disabled, forceUnAuthorizedUser, isAuthenticated],
    );

    const onClickHandler = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        const url = new URL(`${window.location.origin}${routes.getLoginPage()}`);

        url.searchParams.set('redirectUrl', pathnameFromProps || pathname);

        const defaultRedirectUrl = decodeURIComponent(url.toString());

        window.location.assign(redirectUrl || defaultRedirectUrl);
      },
      [disabled, pathname, pathnameFromProps, redirectUrl],
    );

    let params = useMemo(() => ({ ...props }), [props]);

    if (failedAuthCheck) {
      params = { onClick: onClickHandler };
    }

    return React.cloneElement(children, { ...params });
  },
);
